import camelCase from './stringToCamelCase';

const FACET_LABEL_WORD_OVERRIDES = {
    pe: 'PE',
    vce: 'VCE',
    vm: 'VM',
    vcal: 'VCAL',
};

const FACET_LABEL_SMALL_WORDS = new Set(['and', 'or', 'of', 'the', 'at']);

/**
 * Title-case a lowercase Funnelback facet label (e.g. "year 6" → "Year 6").
 *
 * @param {string} optionKey
 * @returns {string}
 */
const formatLowercaseFacetLabel = (optionKey) => {
    let wordIndex = 0;

    return optionKey
        .split(/(\s+|(?<=-)|(?=-)|\/)/)
        .filter((part) => part.length > 0)
        .map((part) => {
            if (/^[\s\-/]+$/.test(part)) {
                return part;
            }

            const lower = part.toLowerCase();

            if (FACET_LABEL_WORD_OVERRIDES[lower]) {
                wordIndex += 1;
                return FACET_LABEL_WORD_OVERRIDES[lower];
            }

            if (wordIndex > 0 && FACET_LABEL_SMALL_WORDS.has(lower)) {
                wordIndex += 1;
                return lower;
            }

            wordIndex += 1;
            return lower.charAt(0).toUpperCase() + lower.slice(1);
        })
        .join('');
};

/**
 * Split a legacy camelCase machine key into words (e.g. "gradeYear6" → "grade Year6").
 *
 * @param {string} optionKey
 * @returns {string}
 */
const formatCamelCaseMachineKey = (optionKey) => {
    return camelCase(optionKey).replace(/([a-z](?=[A-Z]))/g, '$1 ');
};

/**
 * Tiered fallback when no explicit switch-case mapping exists.
 *
 * @param {string} optionKey
 * @returns {string}
 */
const getFacetOptionLabelFallback = (optionKey) => {
    const trimmed = optionKey.trim();

    if (trimmed === '') {
        return '';
    }

    if (/^[a-z0-9\s\-/]+$/.test(trimmed)) {
        return formatLowercaseFacetLabel(trimmed);
    }

    if (/[a-z][A-Z]/.test(trimmed)) {
        return formatCamelCaseMachineKey(trimmed);
    }

    return trimmed;
};

/**
 * Lookup and return the human-friendly label for a given Facet option key.
 * e.g. "stageEarlyChildhood" would return; "Early Childhood"
 *
 * @param {String} optionKey The key to lookup the value for
 * @param {Boolean} usePlural Return the pluralised version of the label
 *
 * @returns String
 */
export const getFacetOptionLabelByKey = (optionKey, usePlural = false) => {
    let optionLabel = '';

    switch (optionKey.trim()) {
        case 'typeLessonPlan':
            if (usePlural) {
                optionLabel = 'Lesson Plans';
            } else {
                optionLabel = 'Lesson Plan';
            }
            break;

        case 'typeProgram':
            if (usePlural) {
                optionLabel = 'Programs';
            } else {
                optionLabel = 'Program';
            }
            break;

        case 'typePolicy':
            if (usePlural) {
                optionLabel = 'Policies';
            } else {
                optionLabel = 'Policy';
            }
            break;

        case 'typeAdditionalResource':
            if (usePlural) {
                optionLabel = 'Additional Resources';
            } else {
                optionLabel = 'Additional Resource';
            }
            break;

        case 'typeUnitsOfWork':
        case 'typeunitsofwork':
        case 'typeUnitOfWork':
        case 'typeunitofwork':
        case 'typeUnitsofWork':
        case 'typeUnitofWork':
            optionLabel = 'Units of Work';
            break;

        case 'curriculumHealthPE':
            optionLabel = 'Health and PE';
            break;

        case 'curriculumEnglish':
            optionLabel = 'English';
            break;

        case 'curriculumMaths':
            optionLabel = 'Maths';
            break;

        case 'curriculumTechnology':
            optionLabel = 'Technology';
            break;

        case 'curriculumScience':
            optionLabel = 'Science';
            break;

        case 'curriculumBiologicalScience':
            optionLabel = 'Biological Science';
            break;

        case 'curriculumScienceHumanEndeavour':
            optionLabel = 'Science as a Human Endeavour';
            break;

        case 'curriculumMedia':
            optionLabel = 'Media';
            break;

        case 'curriculumHumanities':
            optionLabel = 'Humanities';
            break;

        case 'curriculumGeography':
            optionLabel = 'Geography';
            break;

        case 'curriculumBabyAtHome':
            optionLabel = 'Baby At Home';
            break;

        case 'curriculumChildcare':
            optionLabel = 'Childcare';
            break;

        case 'curriculumKindergartenPreschool':
            optionLabel = 'Kindergarten/Preschool';
            break;

        case 'stageEarlyChildhood':
            optionLabel = 'Early Childhood';
            break;

        case 'stagePrimarySchool':
            optionLabel = 'Primary School';
            break;

        case 'stageSecondarySchool':
            optionLabel = 'Secondary School';
            break;

        case 'stageTertiaryEducation':
            optionLabel = 'Tertiary Education';
            break;

        case 'stageLearnerNoviceDriver':
            optionLabel = 'Learner/Novice Driver';
            break;

        case 'gradePrep':
            optionLabel = 'Prep';
            break;

        case 'gradeYear1':
            optionLabel = 'Year 1';
            break;

        case 'gradeYear2':
            optionLabel = 'Year 2';
            break;

        case 'gradeYear3':
            optionLabel = 'Year 3';
            break;

        case 'gradeYear4':
            optionLabel = 'Year 4';
            break;

        case 'gradeYear5':
            optionLabel = 'Year 5';
            break;

        case 'gradeYear6':
            optionLabel = 'Year 6';
            break;

        case 'gradeYear7':
            optionLabel = 'Year 7';
            break;

        case 'gradeYear8':
            optionLabel = 'Year 8';
            break;

        case 'gradeYear9':
            optionLabel = 'Year 9';
            break;

        case 'gradeYear10':
            optionLabel = 'Year 10';
            break;

        case 'gradeYear11':
            optionLabel = 'Year 11';
            break;

        case 'gradeYear12':
            optionLabel = 'Year 12';
            break;

        case 'gradeVCAL':
            optionLabel = 'VCAL';
            break;

        case 'learningModeIncursion':
            optionLabel = 'Incursion';
            break;

        case 'learningModeExcursion':
            optionLabel = 'Excursion';
            break;

        case 'learningModeInClass':
            optionLabel = 'In-Class';
            break;

        case 'learningModeSelfLearning':
            optionLabel = 'Self Learning';
            break;

        case 'learningModeInteractiveLearning':
            optionLabel = 'Interactive Learning';
            break;

        case 'learningModeAtHome':
            optionLabel = 'At-Home';
            break;

        case 'learningModeOnline':
            optionLabel = 'Online';
            break;

        case '1':
            optionLabel = '1';
            break;

        case '10':
            optionLabel = '10';
            break;

        case '5':
            optionLabel = '5';
            break;

        case '50':
            optionLabel = '50';
            break;

        case '500':
            optionLabel = '500';
            break;

        default:
            optionLabel = getFacetOptionLabelFallback(optionKey);
            optionLabel = getFacetOptionLabelFallback(optionKey);
    }

    return optionLabel;
};

/**
 * Normalise a facet value from a URL query string.
 *
 * @param {string} value
 * @returns {string}
 */
export const normalizeFacetQueryValue = (value) => {
    if (typeof value !== 'string') {
        return '';
    }

    try {
        return decodeURIComponent(value.replace(/\+/g, ' ')).trim();
    } catch {
        return value.replace(/\+/g, ' ').trim();
    }
};

/**
 * Remove empty facet values and normalise encoding from URL parameters.
 *
 * @param {string[]} values
 * @returns {string[]}
 */
export const sanitizeFacetQueryValues = (values) => {
    if (!Array.isArray(values)) {
        return [];
    }

    return values.map(normalizeFacetQueryValue).filter((value) => value !== '');
};

/**
 * Check through a given query string and return all values that match a provided key.
 *
 * @param {String} queryString The full query string (or URL). e.g. id=1234&src=home&campaign-ID=r569_WW&f.Type|type=abc&f.Type|type=xyz
 * @param {String} key The key of the options to be matched. e.g. f.Type|type
 *
 * @returns Array of matched values
 */
export const getQueryParameterValuesByKey = (queryString, key) => {
    const queryKeyValues = [];
    let allQueryParameters = [];
    let currentParameter = [];
    let escapedQueryKey = '';
    let valueTest = {};

    // Split all the query values into an Array
    allQueryParameters = queryString.split('&');

    // Escape any special characters in the query key
    escapedQueryKey = key
        .replace(/\./g, '\\.')
        .replace(/\|/g, '%7C')
        .replace(/\+/g, '\\+');

    // Assemble the RegExp
    valueTest = new RegExp(`(?=^|&)${escapedQueryKey}=(.*?)(?=&|$)`);

    // Check all the parameters for a key match and extract the value
    for (let i = 0, j = allQueryParameters.length; i < j; ++i) {
        currentParameter = allQueryParameters[i].match(valueTest);

        if (currentParameter !== null) {
            queryKeyValues.push(currentParameter[1]);
        }
    }

    return queryKeyValues;
};
